import React, { useEffect, useState, useMemo } from 'react';
import { Activity, Sparkles, Trophy, FileCheck, BookOpen, Users } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

/**
 * LiveActivityBar — Bandeau "vivant" qui rotate des messages d'activité
 * pour donner l'impression d'une plateforme active.
 *
 * V1 (MVP) : messages générés localement, déterministes par jour.
 * V2 : appel à `/api/public/activity-pulse` pour récupérer des messages
 *      avec chiffres RÉELS (questions, modules, article à la une...).
 *      Fallback automatique sur la V1 si l'endpoint est indisponible.
 *
 * Design : ligne unique, icône + texte court, bullet pulsant rouge,
 * rotation toutes les 6s avec slide+fade.
 */

// Mapping kind → icône Lucide (pour les messages V2 servis par le backend)
const KIND_ICON = {
  ecsr: Sparkles,
  etm: FileCheck,
  gratuit: Activity,
  questions: Trophy,
  modules: BookOpen,
  magazine: BookOpen,
  plateau: Users,
};

// Parse les **gras** d'un message backend en JSX (markdown léger)
function parseRichText(text) {
  if (!text) return null;
  const parts = String(text).split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => {
    if (p.startsWith('**') && p.endsWith('**')) {
      return <strong key={i}>{p.slice(2, -2)}</strong>;
    }
    return <React.Fragment key={i}>{p}</React.Fragment>;
  });
}

/**
 * LiveActivityBar — Bandeau "vivant" qui rotate des messages d'activité
 * pour donner l'impression d'une plateforme active.
 *
 * MVP : génère localement les messages (semis pseudo-aléatoire stable
 * par jour pour ne pas être ridicule à chaque refresh). Dans une V2,
 * brancher sur `/api/public/activity-pulse` (compteurs réels MongoDB).
 *
 * Design : ligne unique, icône + texte court, bullet pulsant rouge
 * (Live), rotation toutes les 6 s avec slide+fade.
 */

// Petit générateur déterministe basé sur la date (jour + hour bucket)
const dailySeed = () => {
  const d = new Date();
  const day = d.getUTCFullYear() * 10000 + (d.getUTCMonth() + 1) * 100 + d.getUTCDate();
  // bucket de 3h pour un peu de variété au cours de la journée
  const bucket = Math.floor(d.getUTCHours() / 3);
  return day * 10 + bucket;
};
const seededRand = (seed, min, max) => {
  // Mulberry32
  let t = seed + 0x6D2B79F5;
  t = Math.imul(t ^ (t >>> 15), t | 1);
  t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
  const r = ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  return Math.floor(min + r * (max - min + 1));
};

// Génère 6 messages factuels et vérifiables, ordonnés aléatoirement par seed du jour.
// Chaque message est une affirmation honnête sur la plateforme, pas un compteur fictif.
const buildMessages = () => {
  const seed = dailySeed();

  const pool = [
    {
      id: 'ecsr',
      icon: Sparkles,
      color: '#cf2031',
      text: <>Conçu par des moniteurs <strong>agréés ECSR</strong></>,
    },
    {
      id: 'etm',
      icon: FileCheck,
      color: '#f59e0b',
      text: <>Programme officiel <strong>ETM 2026</strong> — conforme arrêté REMC</>,
    },
    {
      id: 'gratuit',
      icon: Activity,
      color: '#16a34a',
      text: <><strong>100 % gratuit</strong> · sans pub · sans tracking · pour toujours</>,
    },
    {
      id: 'magazine',
      icon: Trophy,
      color: '#0891b2',
      text: <><strong>9 dossiers ETM</strong> · 31 enquêtes magazine · 72 articles satellites</>,
    },
    {
      id: 'guide',
      icon: BookOpen,
      color: '#7c3aed',
      text: <>Magazine hebdo · <strong>nouveau guide</strong> chaque semaine</>,
    },
    {
      id: 'plateau',
      icon: Users,
      color: '#cf2031',
      text: <>Plateau interactif · <strong>6 manœuvres officielles</strong> détaillées</>,
    },
  ];

  // Mélange pseudo-aléatoire stable par jour
  return pool
    .map((m, i) => ({ m, k: seededRand(seed + 100 + i, 0, 1_000_000) }))
    .sort((a, b) => a.k - b.k)
    .map((x) => x.m);
};

const ROTATION_MS = 6000;
const TRANSITION_MS = 380;

export default function LiveActivityBar({ className = '', testid = 'live-activity-bar' }) {
  // Pool de fallback (toujours dispo, identique à la V1)
  const fallback = useMemo(() => buildMessages(), []);

  // Pool serveur (V2) — null tant que le fetch n'a pas répondu
  const [serverMessages, setServerMessages] = useState(null);

  // Récupération des messages factuels servis par le backend.
  // En cas d'erreur (réseau, endpoint absent), on retombe automatiquement
  // sur le pool local. Aucun loader visible : le composant affiche
  // immédiatement le pool fallback puis se met à jour si les données
  // serveur arrivent dans les 6 premières secondes.
  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/public/activity-pulse`, {
          signal: controller.signal,
        });
        if (!res.ok) throw new Error('pulse-not-ok');
        const json = await res.json();
        if (cancelled || !Array.isArray(json?.messages) || json.messages.length === 0) return;
        const mapped = json.messages.map((m) => ({
          id: m.id,
          icon: KIND_ICON[m.kind] || Sparkles,
          color: m.color || '#cf2031',
          text: parseRichText(m.text),
        }));
        setServerMessages(mapped);
      } catch (_e) {
        // silencieux : on garde le fallback
      }
    })();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, []);

  const messages = serverMessages || fallback;
  const [idx, setIdx] = useState(0);
  const [phase, setPhase] = useState('in'); // 'in' | 'out'

  useEffect(() => {
    if (messages.length <= 1) return undefined;
    const tick = setInterval(() => {
      setPhase('out');
      window.setTimeout(() => {
        setIdx((i) => (i + 1) % messages.length);
        setPhase('in');
      }, TRANSITION_MS);
    }, ROTATION_MS);
    return () => clearInterval(tick);
  }, [messages.length]);

  if (!messages.length) return null;
  const current = messages[idx];
  const Icon = current.icon;

  return (
    <div
      className={`relative overflow-hidden rounded-full border border-border/40 bg-card/80 backdrop-blur-sm px-3.5 sm:px-4 py-2 flex items-center gap-2.5 sm:gap-3 ${className}`}
      data-testid={testid}
      aria-live="polite"
      aria-atomic="true"
    >
      {/* Live dot pulsant rouge */}
      <span className="relative flex items-center justify-center flex-shrink-0">
        <span
          className="absolute inline-flex h-2 w-2 rounded-full opacity-60 pulse-dot"
          style={{ background: '#cf2031' }}
        />
        <span
          className="relative inline-flex h-2 w-2 rounded-full"
          style={{ background: '#cf2031' }}
        />
      </span>
      <span className="text-[9px] sm:text-[10px] font-black uppercase tracking-[0.18em] text-foreground/55 flex-shrink-0">
        À savoir
      </span>
      <span className="hidden sm:inline-block w-px h-3 bg-border" aria-hidden="true" />

      {/* Slot rotatif */}
      <div className="relative flex-1 min-w-0 overflow-hidden">
        <div
          key={current.id}
          className={phase === 'in' ? 'activity-in' : 'activity-out'}
        >
          <div className="flex items-center gap-2 min-w-0">
            <Icon
              className="w-3.5 h-3.5 flex-shrink-0"
              style={{ color: current.color }}
              aria-hidden="true"
            />
            <span className="text-xs sm:text-sm text-foreground/85 truncate">
              {current.text}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
